/**
 * Password validation utility
 * Enforces: 8+ chars, uppercase, lowercase, number, special character
 */

export interface PasswordCriteria {
  minLength: boolean
  hasUppercase: boolean
  hasLowercase: boolean
  hasNumber: boolean
  hasSpecial: boolean
}

export interface PasswordValidationResult {
  isValid: boolean
  criteria: PasswordCriteria
  criteriaCount: number
  strengthPercent: number
  errors: string[]
}

const SPECIAL_CHARS_REGEX = /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'\/`~]/

export function validatePassword(password: string): PasswordValidationResult {
  const criteria: PasswordCriteria = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: SPECIAL_CHARS_REGEX.test(password)
  }

  const criteriaCount = Object.values(criteria).filter(Boolean).length
  const strengthPercent = (criteriaCount / 5) * 100
  const isValid = criteriaCount === 5

  const errors: string[] = []
  if (!criteria.minLength) errors.push('At least 8 characters')
  if (!criteria.hasUppercase) errors.push('One uppercase letter')
  if (!criteria.hasLowercase) errors.push('One lowercase letter')
  if (!criteria.hasNumber) errors.push('One number')
  if (!criteria.hasSpecial) errors.push('One special character')

  return {
    isValid,
    criteria,
    criteriaCount,
    strengthPercent,
    errors
  }
}

export function getStrengthColor(criteriaCount: number): string {
  if (criteriaCount <= 2) return 'var(--color-error)'
  if (criteriaCount <= 3) return 'var(--color-warning)'
  return 'var(--color-success)'
}

export function getPasswordErrorMessage(errors: string[]): string {
  if (errors.length === 0) return ''
  return `Password must have: ${errors.join(', ')}`
}
